import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BagTile } from '@/components/admin/BagTile';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import { type StagedListing, useRoasterListings } from '@/hooks/useRoasterListings';
import { useRoasters } from '@/hooks/useRoasters';
import { relTime } from '@/lib/crawlRuns';
import { displayHost, platformLabel } from '@/lib/roasterPlatforms';

type ListingFilter = 'all' | 'live' | 'delisted' | 'newCrop';

const FILTERS: { id: ListingFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'live', label: 'Live' },
  { id: 'delisted', label: 'Delisted' },
  { id: 'newCrop', label: 'New crop' },
];

function matchesFilter(listing: StagedListing, filter: ListingFilter): boolean {
  if (filter === 'live') return !listing.delisted;
  if (filter === 'delisted') return listing.delisted;
  if (filter === 'newCrop') return listing.newCrop;
  return true;
}

// Descriptions are stored as plain text; this defends the drawer against rows
// staged before that landed. Not a sanitiser — the value is only ever rendered
// as text, never as HTML.
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Small pill link that opens in a new tab. Used in the row for the captured
// image and the product page — the crawl only records URLs, so we surface them
// as links rather than rendering the remote image (which CSP blocks anyway).
function RowLink({ href, label, testId }: { href: string; label: string; testId: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      className="rounded-md border border-wolisho-brown/[0.15] px-2 py-1 text-[11px] font-bold text-wolisho-brown"
    >
      {label}
    </a>
  );
}

function FlagChip({ kind }: { kind: 'newCrop' | 'delisted' }) {
  const styles: Record<typeof kind, string> = {
    newCrop: 'bg-wolisho-brown/[0.08] text-wolisho-brown',
    delisted: 'bg-wolisho-brown/[0.06] text-wolisho-brown/50',
  };
  const labels: Record<typeof kind, string> = {
    newCrop: 'New crop',
    delisted: 'Delisted',
  };
  return (
    <span
      className={`inline-flex items-center rounded-md px-[7px] py-[3px] text-[10.5px] font-bold ${styles[kind]}`}
    >
      {labels[kind]}
    </span>
  );
}

function listingFlags(listing: StagedListing): ('newCrop' | 'delisted')[] {
  const flags: ('newCrop' | 'delisted')[] = [];
  if (listing.newCrop) flags.push('newCrop');
  if (listing.delisted) flags.push('delisted');
  return flags;
}

function ListingRow({ listing, onOpen }: { listing: StagedListing; onOpen: (id: string) => void }) {
  const flags = listingFlags(listing);
  const productUrl = listing.url.startsWith('http') ? listing.url : `https://${listing.url}`;
  return (
    // The links open in a new tab and must not be nested in the drawer button,
    // so the button and the link column are siblings in a plain row container.
    <div className="flex w-full items-center gap-2.5 px-3.5 py-[11px]">
      <button
        type="button"
        onClick={() => onOpen(listing.id)}
        data-testid={`listing-row-${listing.id}`}
        className="flex min-w-0 flex-1 items-center gap-2.5 border-0 bg-transparent text-left"
      >
        <BagTile label={listing.title} size={38} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-bold text-black">{listing.title}</p>
          <p className="mt-0.5 text-[11.5px] font-bold text-black/40">
            Seen {relTime(listing.firstSeenAt)}
            {!listing.available && ' · sold out'}
          </p>
        </div>
      </button>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {flags.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1">
            {flags.slice(0, 2).map((flag) => (
              <FlagChip key={flag} kind={flag} />
            ))}
          </div>
        )}
        <div className="flex gap-1.5">
          {listing.imageUrl && (
            <RowLink
              href={listing.imageUrl}
              label="Image"
              testId={`listing-image-link-${listing.id}`}
            />
          )}
          <RowLink
            href={productUrl}
            label="Product"
            testId={`listing-product-link-${listing.id}`}
          />
        </div>
      </div>
    </div>
  );
}

function DrawerField({ label, value }: { label: string; value: string | null }) {
  const hasValue = value != null && value !== '';
  return (
    <div className="flex gap-2.5 border-b border-wolisho-brown/[0.06] py-1.5">
      <span className="flex-[0_0_84px] text-[11.5px] font-bold text-wolisho-brown/70">{label}</span>
      <span
        className={
          hasValue ? 'flex-1 text-[13px] text-black' : 'flex-1 text-[13px] font-bold text-black/30'
        }
      >
        {hasValue ? value : '—'}
      </span>
    </div>
  );
}

function DetailDrawer({ listing, onClose }: { listing: StagedListing | null; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  // Kept in a ref so the effect can depend only on `listing`: a fresh onClose
  // identity each render must not re-run focus move / restore.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Modal focus handling: on open, remember the trigger and move focus into the
  // dialog; Escape closes; on close, restore focus to the trigger.
  useEffect(() => {
    if (!listing) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus();
    };
  }, [listing]);

  if (!listing) return null;
  const flags = listingFlags(listing);
  const description = stripHtml(listing.description);
  return (
    <div className="fixed inset-0 z-50 flex items-end">
      {/* Backdrop closes on click. It's not a tab stop (tabIndex -1) and is
          hidden from AT — the dialog is aria-modal and Escape is the keyboard
          affordance — so clicks on the dialog (a positioned sibling) never
          reach it, no stop-propagation needed. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 border-0 bg-[rgba(24,20,16,0.4)] p-0"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={listing.title}
        tabIndex={-1}
        className="relative max-h-[88%] w-full overflow-auto rounded-t-2xl bg-wolisho-cream p-[18px] outline-hidden"
        data-testid="listing-detail-drawer"
      >
        <div className="mx-auto mb-3.5 h-1 w-9 rounded-full bg-wolisho-brown/[0.15]" />
        <div className="mb-2.5 flex gap-3">
          <BagTile label={listing.title} size={60} />
          <div className="min-w-0 flex-1">
            {flags.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1.5">
                {flags.map((flag) => (
                  <FlagChip key={flag} kind={flag} />
                ))}
              </div>
            )}
            <h2 className="text-[17px] font-bold leading-tight text-black">{listing.title}</h2>
          </div>
        </div>

        <div className="mb-3.5">
          <DrawerField label="Shop" value={displayHost(listing.url)} />
          <DrawerField label="Platform" value={platformLabel(listing.platform)} />
          <DrawerField label="First seen" value={relTime(listing.firstSeenAt)} />
          <DrawerField label="SKU root" value={listing.skuRoot} />
          <DrawerField label="Availability" value={listing.available ? 'In stock' : 'Sold out'} />
          {description && <DrawerField label="Feed text" value={description} />}
        </div>

        {listing.extractedDescription && (
          <div className="mb-3.5" data-testid="listing-extracted-description">
            <p className="mb-1 text-[11.5px] font-bold text-wolisho-brown/70">
              Extracted description
            </p>
            <p className="whitespace-pre-line text-[13px] leading-relaxed text-black">
              {listing.extractedDescription}
            </p>
          </div>
        )}

        <a
          href={listing.url.startsWith('http') ? listing.url : `https://${listing.url}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border border-wolisho-brown/25 px-3.5 py-2 text-[13px] font-bold text-wolisho-brown"
        >
          Open on shop
        </a>
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

// Staff-only per-roaster review of the listings a crawl staged (design:
// Listings Review). Read-only — approval/promotion is deferred (RFC 008).
// Rendering is gated by the is_admin claim in App.tsx; the real enforcement is
// the API's admin middleware, which 404s /admin/crawl/* for non-admins.
export function ListingsReviewPage() {
  const navigate = useNavigate();
  const { roasterId } = useParams<{ roasterId: string }>();
  const { listings, isLoading, error } = useRoasterListings(roasterId);
  const { roasters } = useRoasters();
  // Default to "Live": delisted listings are gone from the shop, so the review
  // queue leads with the coffees actually worth reviewing.
  const [filter, setFilter] = useState<ListingFilter>('live');
  const [openId, setOpenId] = useState<string | null>(null);

  const roaster = useMemo(
    () => roasters?.find((entry) => entry.id === roasterId) ?? null,
    [roasters, roasterId],
  );
  const items = useMemo(() => listings ?? [], [listings]);
  // Headline count is the live (non-delisted) set; delisted is a secondary note.
  const liveCount = useMemo(() => items.filter((item) => !item.delisted).length, [items]);
  const delistedCount = items.length - liveCount;
  const visible = useMemo(() => items.filter((item) => matchesFilter(item, filter)), [items, filter]);
  const openListing = items.find((item) => item.id === openId) ?? null;

  return (
    <Layout>
      <div className="mx-auto max-w-[640px] px-4 pb-10" data-testid="listings-review-page">
        <div className="flex items-center gap-2 pb-1 pt-5">
          <button
            type="button"
            onClick={() => void navigate('/admin/roasters')}
            aria-label="Back to roaster inventory"
            data-testid="listings-review-back"
            className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-wolisho-brown opacity-50 transition-opacity hover:opacity-80"
          >
            <BackIcon />
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-wolisho-brown/60">
            Review
          </span>
        </div>

        <h1 className="mt-1 text-2xl font-bold text-black">{roaster?.name ?? 'Roaster'}</h1>

        {isLoading ? (
          <LoadingSpinner label="Loading listings" testId="listings-review-loading" />
        ) : error ? (
          <p className="mt-4 text-sm text-black/50" data-testid="listings-review-error">
            Couldn&apos;t load staged listings. Refresh to try again.
          </p>
        ) : (
          <>
            <p className="mb-4 mt-0.5 text-sm text-black/50" data-testid="listings-review-subtitle">
              {liveCount > 0 ? (
                <span className="font-bold text-black">{liveCount}</span>
              ) : (
                'Nothing'
              )}{' '}
              awaiting review
              {delistedCount > 0 && <> · {delistedCount} delisted</>} ·{' '}
              <Link to="/admin/crawl-runs">view crawls</Link>
            </p>

            {items.length === 0 ? (
              <div
                className="rounded-xl bg-wolisho-card px-4 py-12 text-center"
                data-testid="listings-review-empty"
              >
                <p className="mb-1 text-sm font-bold text-black/55">All caught up</p>
                <p className="text-[13px] text-black/40">
                  Nothing awaiting review for {roaster?.name ?? 'this roaster'}.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {FILTERS.map((entry) => {
                    const active = filter === entry.id;
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setFilter(entry.id)}
                        className={
                          active
                            ? 'rounded-full border-0 bg-wolisho-brown px-3 py-1.5 text-[12.5px] font-bold text-white'
                            : 'rounded-full border border-wolisho-brown/[0.15] bg-transparent px-3 py-1.5 text-[12.5px] font-bold text-wolisho-brown'
                        }
                      >
                        {entry.label}
                      </button>
                    );
                  })}
                </div>

                <div className="overflow-hidden rounded-xl bg-wolisho-card">
                  <div className="border-b border-wolisho-brown/[0.08] px-3.5 py-2.5">
                    <span className="text-[11.5px] font-bold uppercase tracking-[0.04em] text-wolisho-brown/50">
                      {visible.length} listing{visible.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {visible.length > 0 ? (
                    visible.map((listing, index) => (
                      <div key={listing.id}>
                        {index > 0 && <div className="mx-3.5 h-px bg-wolisho-brown/[0.06]" />}
                        <ListingRow listing={listing} onOpen={setOpenId} />
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-10 text-center">
                      <p className="text-[13.5px] font-bold text-black/45">Nothing in this filter</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <DetailDrawer listing={openListing} onClose={() => setOpenId(null)} />
    </Layout>
  );
}
